import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import React from "react";
import { getMemberInfo } from "@/hooks/useMembers";
import * as TablerIcons from "@tabler/icons-react";
import navigationConfig from "./profile-navigation.json";

interface NavigationItem {
  id: string;
  labelKey: string;
  path: string;
  iconName?: string;
  derivedTypes: string[];
  addedPadding?: boolean;
  defaultState?: string;
}

type NavigationSection = "basicNav" | "userProfileNav" | "healthProfileNav";

type NavigationConfig = {
  [key in NavigationSection]: NavigationItem[];
};

function mapIcons(items: NavigationItem[], t?: any) {
  return items.map((item) => {
    let icon: React.ComponentType<any> | null = null;
    if (item.iconName) {
      const iconName = `Icon${
        item.iconName.charAt(0).toUpperCase() + item.iconName.slice(1)
      }` as keyof typeof TablerIcons;
      const iconComponent = TablerIcons[iconName];
      icon = (
        typeof iconComponent === "function" ? iconComponent : null
      ) as React.ComponentType<any> | null;
    }
    return {
      ...item,
      icon,
      label: item.labelKey && t ? t(item.labelKey) : (item as any).label,
    };
  });
}

export function useProfileNavigation() {
  const member = getMemberInfo();
  const { t } = useTranslation();

  // Memoize the filtered navigation arrays
  const navigation = useMemo(() => {
    const { derivedMemberType } = member;

    const filterAndMap = (items: NavigationItem[]) =>
      mapIcons(
        items.filter((item) => item.derivedTypes.includes(derivedMemberType)),
        t
      );

    return {
      basicNav: filterAndMap(navigationConfig.basicNav),
      userProfileNav: filterAndMap(navigationConfig.userProfileNav),
      healthProfileNav: filterAndMap(navigationConfig.healthProfileNav),
    };
  }, [member.derivedMemberType, t]);

  return navigation;
}

export function profileNavigation(
  section: NavigationSection = "basicNav",
  t?: any
) {
  const navArr = (navigationConfig as NavigationConfig)[section] || [];
  return mapIcons(navArr, t);
}
